/**
 * API Client Context
 *
 * Provides a way for projects to inject their generated API client
 * so that framework components can call reducers without depending
 * on project-specific code.
 *
 * This enables framework modules like shadcn-components to call
 * reducers like `User.updatePersonalUserData` without directly
 * importing the project's API client.
 *
 * The generatedClient is typically provided via FrontendProvision.generatedClient
 * and automatically wired up by AtomFrameworkProvider.
 */

import type {
  IPrivateUserData,
  UserPublicID,
  UserState,
} from "@atoms/atom-types";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import { AtomStoreContext } from "./AtomStoreProvider";
import type { GeneratedApiClient } from "./FrontendProvision";
import { useCombinedContext } from "./useCombinedContext";

/**
 * Data required to create a user.
 * Matches the personalUserData parameter of Global.createUser reducer.
 */
export interface CreateUserData {
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  state: UserState;
}

/**
 * Data required to invite a user.
 * Matches the parameters of Global.inviteUser reducer.
 */
export interface InviteUserData {
  email: string;
  firstName: string;
  lastName: string;
  /**
   * URL to redirect to after magic link verification (for existing users).
   */
  callbackURL: string;
  /**
   * URL to redirect to after magic link verification (for new users, typically onboarding page).
   */
  newUserCallbackURL: string;
}

/**
 * Context value for the API client.
 */
interface ApiClientContextValue {
  /**
   * The raw generated API client object.
   */
  generatedClient: GeneratedApiClient;
}

const ApiClientContext = createContext<ApiClientContextValue | null>(null);

export interface ApiClientProviderProps {
  children: ReactNode;
  /**
   * The project's generated API client object.
   * This is the raw object with `.handler` functions, not the hooked version.
   */
  generatedClient: GeneratedApiClient;
}

/**
 * Provider for the API client.
 *
 * Wrap your app with this provider (inside AtomStoreProvider) to enable
 * framework components to call reducers.
 */
export function ApiClientProvider({
  children,
  generatedClient,
}: Readonly<ApiClientProviderProps>) {
  const value = useMemo(() => ({ generatedClient }), [generatedClient]);

  return (
    <ApiClientContext.Provider value={value}>
      {children}
    </ApiClientContext.Provider>
  );
}

/**
 * Standard User API interface that framework components can depend on.
 *
 * This is what `useStandardUserApi()` returns.
 */
export interface StandardUserApi {
  /**
   * Update the personal user data for the user in the current context.
   */
  updatePersonalData: (update: Partial<IPrivateUserData>) => Promise<boolean>;

  /**
   * Create a new user in the Atom Framework.
   * Returns the created user's ID.
   *
   * This method is only available if the project has a `Global.createUser` reducer.
   */
  createUser?: (data: CreateUserData) => Promise<UserPublicID>;

  /**
   * Invite a new user by creating them in the system and sending an invitation email.
   * This is a single transactional operation - if any step fails, the entire operation is rolled back.
   * Returns the created user's ID.
   *
   * This method is only available if the project has a `Global.inviteUser` reducer.
   */
  inviteUser?: (data: InviteUserData) => Promise<UserPublicID>;
}

/**
 * Hook to get the standard User API methods.
 *
 * Returns null if:
 * - ApiClientProvider is not present
 * - AtomStoreProvider is not present
 * - The API client doesn't have a User relation with updatePersonalData
 *
 * @example
 * ```tsx
 * const userApi = useStandardUserApi();
 * if (userApi) {
 *   await userApi.updatePersonalData({ firstName: "John" });
 * }
 * ```
 */
export function useStandardUserApi(): StandardUserApi | null {
  const apiClientContext = useContext(ApiClientContext);
  const atomStore = useContext(AtomStoreContext);
  const combinedContext = useCombinedContext();

  return useMemo(() => {
    if (!apiClientContext?.generatedClient || !atomStore) {
      return null;
    }

    const { generatedClient } = apiClientContext;
    const apolloClient = atomStore.getApolloClient();
    const { onError } = atomStore;

    // Check if User.updatePersonalData exists
    const userRelation = generatedClient.User;
    const updateHandler = userRelation?.updatePersonalData?.handler;
    if (!updateHandler) {
      console.warn(
        "[useStandardUserApi] API client does not have User.updatePersonalData",
      );
      return null;
    }

    // Check Global reducers (optional)
    const globalRelation = generatedClient.Global;
    const createUserHandler = globalRelation?.createUser?.handler;
    const inviteUserHandler = globalRelation?.inviteUser?.handler;

    const api: StandardUserApi = {
      updatePersonalData: async (
        update: Partial<IPrivateUserData>,
      ): Promise<boolean> => {
        return updateHandler(apolloClient, onError, combinedContext, update);
      },
    };

    // Add createUser if available
    if (createUserHandler) {
      api.createUser = async (data: CreateUserData): Promise<UserPublicID> => {
        return createUserHandler(apolloClient, onError, combinedContext, data);
      };
    }

    // Add inviteUser if available
    if (inviteUserHandler) {
      api.inviteUser = async (data: InviteUserData): Promise<UserPublicID> => {
        return inviteUserHandler(
          apolloClient,
          onError,
          combinedContext,
          data.email,
          data.firstName,
          data.lastName,
          data.callbackURL,
          data.newUserCallbackURL,
        );
      };
    }

    return api;
  }, [apiClientContext, atomStore, combinedContext]);
}
